import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Server,
  LogOut,
  RefreshCw,
  Network,
  History,
  AlertCircle,
  CheckCircle2,
  Globe,
  Layers,
  Loader2,
  Search,
  Shield,
  KeyRound,
} from 'lucide-react';
import { Input } from './ui/input';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ onLogout, isAdmin, branding = {} }) => {
  const navigate = useNavigate();
  const [vpsList, setVpsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedVps, setSelectedVps] = useState(null);
  const [availableIps, setAvailableIps] = useState([]);
  const [poolName, setPoolName] = useState('');
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [ipHistory, setIpHistory] = useState([]);
  const [changingIp, setChangingIp] = useState(false);
  const [loadingIps, setLoadingIps] = useState(false);
  const [selectedIps, setSelectedIps] = useState([]);
  const [changeStep, setChangeStep] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // Filter VPS by search query
  const filteredVps = vpsList.filter(vps => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      vps.hostname?.toLowerCase().includes(q) ||
      vps.vpsid?.toString().includes(q) ||
      vps.server_name?.toLowerCase().includes(q) ||
      vps.current_ips?.some(ip => ip.includes(q))
    );
  });

  useEffect(() => {
    fetchUser();
    fetchVpsList();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to fetch user info');
    }
  };

  const fetchVpsList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vps`);
      setVpsList(response.data);
    } catch (error) {
      toast.error('Failed to fetch VPS list');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIps = async (vpsid) => {
    try {
      const response = await axios.get(`${API}/vps/${vpsid}/available-ips`);
      setAvailableIps(response.data.available_ips);
      setPoolName(response.data.pool_name || 'Unknown');
      return response.data.available_ips;
    } catch (error) {
      toast.error('Failed to fetch available IPs');
      return [];
    }
  };

  const fetchIpHistory = async (vpsid) => {
    try {
      const response = await axios.get(`${API}/vps/${vpsid}/ip-history`);
      setIpHistory(response.data.history);
    } catch (error) {
      toast.error('Failed to fetch IP history');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) { toast.error('Fill in both fields'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw });
      toast.success('Password changed successfully');
      setPwDialogOpen(false);
      setCurrentPw(''); setNewPw('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally { setSavingPw(false); }
  };

  const handleOpenChangeDialog = async (vps) => {
    setSelectedVps(vps);
    // Initialize selectedIps with empty strings for each IP slot
    setSelectedIps(new Array(vps.ip_count || vps.current_ips.length).fill(''));
    setAvailableIps([]);
    setPoolName('');
    setChangeDialogOpen(true);
    setLoadingIps(true);
    const ips = await fetchAvailableIps(vps.vpsid);
    setLoadingIps(false);
    if (ips.length === 0) {
      toast.error('No available IPs in this pool');
    }
  };

  const handleIpSelect = (index, ip) => {
    setSelectedIps(prev => {
      const updated = [...prev];
      updated[index] = ip;
      return updated;
    });
  };

  const handleChangeIp = async () => {
    const ipCount = selectedVps?.ip_count || selectedVps?.current_ips?.length || 1;
    const filledIps = selectedIps.filter(ip => ip !== '');
    
    if (filledIps.length !== ipCount) {
      toast.error(`Please select ${ipCount} IP address(es)`);
      return;
    }

    // Check for duplicates
    if (new Set(filledIps).size !== filledIps.length) {
      toast.error('Duplicate IPs selected. Each IP must be unique.');
      return;
    }

    setChangingIp(true);
    setChangeStep('Changing IP address...');
    
    try {
      setChangeStep('Applying new IP configuration...');
      await new Promise(r => setTimeout(r, 500));
      
      const response = await axios.post(`${API}/vps/${selectedVps.vpsid}/change-ip`, {
        new_ips: filledIps,
      });

      setChangeStep('');
      toast.success(response.data.message || 'IP changed and VPS restarted!');
      setChangeDialogOpen(false);
      fetchVpsList();
    } catch (error) {
      setChangeStep('');
      toast.error(error.response?.data?.detail || 'Failed to change IP');
    } finally {
      setChangingIp(false);
    }
  };

  const handleOpenHistory = async (vps) => {
    setSelectedVps(vps);
    await fetchIpHistory(vps.vpsid);
    setHistoryDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || 'unknown';
    if (s === 'online' || s === 'running') {
      return <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Online</Badge>;
    } else if (s === 'offline' || s === 'stopped') {
      return <Badge className="bg-red-600 text-white"><AlertCircle className="w-3 h-3 mr-1" />Offline</Badge>;
    }
    return <Badge className="bg-yellow-600 text-white"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
  };

  // Get IPs already selected in other slots (to prevent duplicates)
  const getUsedIps = (currentIndex) => {
    return selectedIps.filter((ip, i) => i !== currentIndex && ip !== '');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur border-b border-purple-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="h-10 rounded" />
              ) : (
                <div className="flex items-center justify-center w-10 h-10 bg-purple-600 rounded-lg">
                  <Server className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">{branding.app_title || 'VPS IP Manager'}</h1>
                <p className="text-sm text-purple-300">Manage your virtual servers</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="border-purple-600 text-purple-300 hover:bg-purple-700 hover:text-white"
                  data-testid="admin-panel-button"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPwDialogOpen(true); setCurrentPw(''); setNewPw(''); }}
                className="text-purple-300 hover:text-white hover:bg-purple-700"
                data-testid="change-password-button"
              >
                <KeyRound className="w-4 h-4 mr-1" />
                Password
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-purple-300">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="text-purple-300 hover:text-white hover:bg-purple-700"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Your VPS Instances</h2>
            <p className="text-purple-300 mt-1">
              {filteredVps.length} of {vpsList.length} server(s)
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-purple-400" />
              <Input
                placeholder="Search by name or IP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-slate-700/50 border-purple-600 text-white placeholder:text-purple-400"
                data-testid="vps-search-input"
              />
            </div>
            <Button
              onClick={fetchVpsList}
              variant="outline"
              className="border-purple-600 text-purple-300 hover:bg-purple-700 hover:text-white shrink-0"
              data-testid="refresh-vps-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 text-purple-500 animate-spin" />
          </div>
        ) : vpsList.length === 0 ? (
          <Card className="border-purple-700 bg-slate-800/50 backdrop-blur">
            <CardContent className="py-20 text-center">
              <Server className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No VPS Found</h3>
              <p className="text-purple-300">No VPS instances are assigned to your account.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVps.map((vps) => (
              <Card
                key={vps.id}
                className="border-purple-700 bg-slate-800/50 backdrop-blur hover:border-purple-500 transition-colors"
                data-testid={`vps-card-${vps.vpsid}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-white text-lg truncate">{vps.hostname}</CardTitle>
                      <CardDescription className="text-purple-300 mt-1">
                        VPS ID: {vps.vpsid} {vps.server_name && `| ${vps.server_name}`}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-2">
                      {getStatusBadge(vps.status)}
                      {vps.ip_count > 1 && (
                        <Badge className="bg-blue-600 text-white">
                          <Layers className="w-3 h-3 mr-1" />{vps.ip_count} IPs
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-purple-300">
                      <Globe className="w-4 h-4 mr-2 shrink-0" />
                      {vps.ip_count > 1 ? 'IP Addresses' : 'Current IP Address'}
                    </div>
                    {vps.current_ips && vps.current_ips.length > 0 ? (
                      <div className="space-y-1">
                        {vps.current_ips.map((ip, idx) => (
                          <div key={idx} className="font-mono text-white text-sm bg-slate-700/50 px-3 py-1.5 rounded">
                            {ip}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-purple-400 italic">No IP assigned</div>
                    )}
                  </div>

                  <Separator className="bg-purple-700" />

                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleOpenChangeDialog(vps)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid={`change-ip-button-${vps.vpsid}`}
                    >
                      <Network className="w-4 h-4 mr-2" />
                      Change IP{vps.ip_count > 1 ? 's' : ''}
                    </Button>
                    <Button
                      onClick={() => handleOpenHistory(vps)}
                      variant="outline"
                      className="border-purple-600 text-purple-300 hover:bg-purple-700 hover:text-white"
                      data-testid={`view-history-button-${vps.vpsid}`}
                    >
                      <History className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      {branding.footer_text && (
        <footer className="border-t border-purple-700 bg-slate-800/30 py-4 text-center">
          <p className="text-sm text-purple-400">{branding.footer_text}</p>
        </footer>
      )}

      {/* Change IP Dialog */}
      <Dialog open={changeDialogOpen} onOpenChange={(open) => { if (!changingIp) setChangeDialogOpen(open); }}>
        <DialogContent className="bg-slate-800 border-purple-700 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle>Change IP Address{(selectedVps?.ip_count || 0) > 1 ? 'es' : ''}</DialogTitle>
            <DialogDescription className="text-purple-300">
              {selectedVps?.hostname} — Pool: <span className="font-semibold text-purple-200">{poolName}</span>
            </DialogDescription>
          </DialogHeader>

          {/* Progress overlay */}
          {changingIp && (
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg" data-testid="change-ip-progress">
              <Loader2 className="h-10 w-10 text-purple-500 animate-spin mb-4" />
              <p className="text-white font-medium text-lg">Changing IP...</p>
              <p className="text-purple-300 text-sm mt-2">{changeStep || 'Please wait...'}</p>
              <div className="mt-4 w-64">
                <div className="bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div className="bg-purple-500 h-full rounded-full animate-pulse" style={{width: '100%'}}></div>
                </div>
              </div>
              <p className="text-purple-400 text-xs mt-3">VPS will be restarted automatically</p>
            </div>
          )}

          <div className="space-y-4 py-4">
            {/* Current IPs */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-purple-200">Current IP{(selectedVps?.ip_count || 0) > 1 ? 's' : ''}</label>
              <div className="space-y-1">
                {selectedVps?.current_ips?.map((ip, idx) => (
                  <div key={idx} className="font-mono text-sm text-purple-300 bg-slate-700/50 px-3 py-1.5 rounded">
                    {ip}
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-purple-700" />

            {/* New IP selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-purple-200">
                New IP Address{(selectedVps?.ip_count || 0) > 1 ? `es (${selectedVps?.ip_count} required)` : ''}
              </label>

              {loadingIps ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 text-purple-500 animate-spin mr-3" />
                  <span className="text-purple-300">Loading available IPs...</span>
                </div>
              ) : (
                <>
                  {Array.from({ length: selectedVps?.ip_count || selectedVps?.current_ips?.length || 1 }).map((_, idx) => {
                    const usedIps = getUsedIps(idx);
                    const filteredIps = availableIps.filter(ip => !usedIps.includes(ip.ip));
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        {(selectedVps?.ip_count || 0) > 1 && (
                          <span className="text-xs text-purple-400 w-8 shrink-0">IP {idx + 1}</span>
                        )}
                        <Select value={selectedIps[idx] || ''} onValueChange={(val) => handleIpSelect(idx, val)}>
                          <SelectTrigger className="bg-slate-700 border-purple-600 flex-1" data-testid={`ip-select-${idx}`}>
                            <SelectValue placeholder="Select an IP address" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-700 border-purple-600 max-h-60">
                            {filteredIps.map((ipData) => (
                              <SelectItem key={ipData.ip} value={ipData.ip}>
                                <span className="font-mono">{ipData.ip}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                  <p className="text-xs text-purple-400">
                    {availableIps.length} IP(s) available in {poolName}
                  </p>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangeDialogOpen(false)}
              className="border-purple-600 text-purple-300"
              disabled={changingIp}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeIp}
              disabled={changingIp || loadingIps || selectedIps.filter(ip => ip !== '').length !== (selectedVps?.ip_count || selectedVps?.current_ips?.length || 1)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-change-ip-button"
            >
              {changingIp ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing...</>
              ) : (
                <>Change IP{(selectedVps?.ip_count || 0) > 1 ? 's' : ''}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IP History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-slate-800 border-purple-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>IP Change History</DialogTitle>
            <DialogDescription className="text-purple-300">
              {selectedVps?.hostname}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="ip-history-list">
            {ipHistory.length === 0 ? (
              <div className="text-center py-8 text-purple-300">No IP changes recorded yet</div>
            ) : (
              ipHistory.map((log, index) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-purple-700"
                  data-testid={`history-entry-${index}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-mono text-sm truncate">{log.old_ip}</span>
                      <span className="text-purple-400 shrink-0">-&gt;</span>
                      <span className="font-mono text-sm text-purple-300 truncate">{log.new_ip}</span>
                    </div>
                    <div className="text-xs text-purple-400">
                      {new Date(log.changed_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="ml-2 shrink-0">
                    {log.success ? (
                      <Badge className="bg-green-600 text-white">
                        <CheckCircle2 className="w-3 h-3 mr-1" />OK
                      </Badge>
                    ) : (
                      <Badge className="bg-red-600 text-white">
                        <AlertCircle className="w-3 h-3 mr-1" />Fail
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setHistoryDialogOpen(false)} className="bg-purple-600 hover:bg-purple-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent className="bg-slate-800 border-purple-700 text-white">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription className="text-purple-300">Update your account password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-purple-200">Current Password</label>
              <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="bg-slate-700 border-purple-600 text-white" data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-purple-200">New Password</label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-slate-700 border-purple-600 text-white" placeholder="Min 6 characters" data-testid="new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialogOpen(false)} className="border-purple-600 text-purple-300">Cancel</Button>
            <Button onClick={handleChangePassword} disabled={savingPw} className="bg-purple-600 hover:bg-purple-700" data-testid="save-password-btn">
              {savingPw ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
